import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { ExportButton } from "@/components/ExportButton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Search, Loader2, Users, Phone, Mail, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS = { CONFIRMED: "Confirmée", CHECKED_IN: "Enregistrée", IN_PROGRESS: "En cours", COMPLETED: "Terminée", CANCELLED: "Annulée", NO_SHOW: "Absent" };
const LIMIT = 25;

export default function Clients() {
  const { orgId, can } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["customers", orgId, q, page],
    queryFn: async () => (await api.get("/api/customers", { params: { organization_id: orgId, q: q || undefined, limit: LIMIT, skip: page * LIMIT } })).data,
    enabled: !!orgId,
  });
  const rows = data?.customers || [];
  const total = data?.total || 0;
  const currency = data?.currency || "MAD";
  const m = (c) => formatMoney(c || 0, currency);

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">{total} client{total > 1 ? "s" : ""}</p>
        </div>
        {can("finance.view") && <ExportButton kind="customers" orgId={orgId} testid="clients-export-button" />}
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Rechercher un nom, téléphone, e-mail…" data-testid="clients-search"
          className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="clients-empty">
          <Users className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-base font-semibold text-slate-700">Aucun client pour le moment</p>
          <p className="mt-1 text-sm text-slate-400">Vos clients apparaissent ici dès que vous créez une réservation.</p>
          <button onClick={() => nav("/reservations?new=1")} data-testid="clients-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700">
            <ArrowRight className="h-4 w-4" /> Nouvelle réservation
          </button>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>{["Client", "Téléphone", "Réservations", "Total réservé", "Encaissé"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => setSel(c.id)} data-testid={`client-row-${c.id}`} className="cursor-pointer hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.name}{c.email && <span className="ml-2 text-xs text-slate-400">{c.email}</span>}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{c.phone}</td>
                    <td className="px-4 py-3 text-slate-700">{c.stats?.bookings_count ?? 0}{c.stats?.participants ? <span className="ml-1 text-xs text-slate-400">· {c.stats.participants} part.</span> : null}</td>
                    <td className="px-4 py-3 font-mono text-slate-900">{m(c.stats?.reserved_cents)}</td>
                    <td className="px-4 py-3 font-mono font-medium text-emerald-700">{m(c.stats?.paid_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Page {page + 1} / {Math.max(1, Math.ceil(total / LIMIT))}</p>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="clients-prev" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Précédent</button>
              <button disabled={(page + 1) * LIMIT >= total} onClick={() => setPage((p) => p + 1)} data-testid="clients-next" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">Suivant <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </>
      )}

      <ClientDetail id={sel} open={!!sel} onClose={() => setSel(null)} nav={nav} />
    </div>
  );
}

function ClientDetail({ id, open, onClose, nav }) {
  const { orgId } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["customer", orgId, id],
    queryFn: async () => (await api.get(`/api/customers/${id}`, { params: { organization_id: orgId } })).data,
    enabled: !!orgId && !!id,
  });
  const c = data?.customer;
  const currency = data?.currency || "MAD";
  const m = (v) => formatMoney(v || 0, currency);
  const s = data?.stats || {};

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="client-detail">
        <SheetHeader>
          <SheetTitle>{c?.name || "Client"}</SheetTitle>
          <SheetDescription>Fiche client · historique et valeurs</SheetDescription>
        </SheetHeader>
        {isLoading || !c ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Phone className="h-4 w-4 text-slate-400" /> {c.phone}</div>
              {c.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {c.email}</div>}
              {c.country && <div className="text-slate-500">Nationalité : {c.country}</div>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Réservations" value={s.bookings_count ?? 0} testid="client-stat-bookings" />
              <Stat label="Total réservé" value={m(s.reserved_cents)} testid="client-stat-reserved" />
              <Stat label="Encaissé" value={m(s.paid_cents)} tone="text-emerald-700" testid="client-stat-paid" />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Historique des réservations</p>
              {(data.bookings || []).length === 0 ? (
                <p className="text-sm text-slate-400">Aucune réservation.</p>
              ) : (
                <div className="space-y-2">
                  {data.bookings.map((b) => (
                    <button key={b.id} onClick={() => { onClose(); nav(`/reservations?open=${b.id}`); }} data-testid={`client-booking-${b.id}`}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-sm hover:bg-slate-50">
                      <div>
                        <span className="font-mono font-medium text-slate-800">{b.booking_reference}</span>
                        <span className="ml-2 text-slate-500">{b.booking_date} · {b.activity_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{STATUS[b.status] || b.status}</span>
                        <span className="font-mono text-slate-700">{m(b.total_amount_cents)}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Stat = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 text-center" data-testid={testid}>
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`mt-1 font-mono text-base font-semibold ${tone}`}>{value}</p>
  </div>
);
